import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { AlertTriangle, ArrowRight, Check, X, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudySession() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const location = useLocation();
  
  const [sessionData, setSessionData] = useState(location.state?.initialData || null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionData) {
      startCase();
    }
  }, []);

  const startCase = async () => {
    try {
      const res = await fetch(`${API}/api/case-studies/${caseId}/start`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to start case');
      const data = await res.json();
      setSessionData(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOptionSelect = (optionId) => {
    const question = sessionData?.stage?.question;
    const maxSelections = question?.max_selections || 1;
    
    setValidationError(null);
    
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(prev => prev.filter(id => id !== optionId));
    } else {
      if (selectedOptions.length >= maxSelections) {
        if (maxSelections === 1) {
          setSelectedOptions([optionId]);
        }
      } else {
        setSelectedOptions(prev => [...prev, optionId]);
      }
    }
  };

  const handleSubmit = async () => {
    const question = sessionData?.stage?.question;
    const requiredSelections = question?.required_selections || 1;
    
    if (selectedOptions.length !== requiredSelections) {
      setValidationError(
        selectedOptions.length < requiredSelections
          ? `Please select exactly ${requiredSelections} option${requiredSelections > 1 ? 's' : ''}.`
          : `Too many selections. Please select exactly ${requiredSelections} option${requiredSelections > 1 ? 's' : ''}.`
      );
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setValidationError(null);
    
    try {
      const res = await fetch(`${API}/api/case-studies/${caseId}/submit-stage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attempt_id: sessionData.attempt_id,
          stage_id: sessionData.stage.stage_id,
          selected_option_ids: selectedOptions
        })
      });
      
      if (!res.ok) throw new Error('Failed to submit answer');
      
      const data = await res.json();
      
      if (data.status === 'completed') {
        navigate(`/case-studies/${caseId}/results/${sessionData.attempt_id}`, {
          state: { results: data }
        });
      } else {
        setSessionData({
          ...sessionData,
          current_stage_index: data.current_stage_index,
          stage: data.stage,
          is_last_stage: data.is_last_stage
        });
        setSelectedOptions([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = async () => {
    navigate('/case-studies');
  };

  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="bg-white border-slate-200 max-w-md">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Error</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => navigate('/case-studies')} variant="outline">
              Back to Case Studies
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading case...</div>
      </div>
    );
  }

  const stage = sessionData.stage;
  const question = stage?.question;
  const progress = ((sessionData.current_stage_index + 1) / sessionData.total_stages) * 100;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header - Fixed position removed to prevent overlay issues */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-semibold truncate text-slate-900">{sessionData.case_title}</h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExitDialog(true)}
              className="text-slate-500 hover:text-slate-900"
              data-testid="exit-case-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Exit
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <Progress value={progress} className="flex-1 h-2" />
            <span className="text-sm text-slate-500 whitespace-nowrap">
              Stage {sessionData.current_stage_index + 1} of {sessionData.total_stages}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {/* Stage Header */}
        <div className="mb-6">
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 mb-2">
            {stage.title}
          </Badge>
        </div>

        {/* Clinical Vignette */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-slate-500 text-sm uppercase tracking-wider">
              Clinical Scenario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg text-slate-800 leading-relaxed" data-testid="vignette-text">
              {stage.vignette}
            </p>
          </CardContent>
        </Card>

        {/* Question */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-slate-900">
              {question.prompt}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {question.required_selections === question.max_selections 
                ? `Select exactly ${question.required_selections} option${question.required_selections > 1 ? 's' : ''}`
                : `Select ${question.required_selections} to ${question.max_selections} options`
              }
              <span className="text-slate-400 ml-2">
                ({selectedOptions.length} selected)
              </span>
            </p>
          </CardHeader>
          <CardContent>
            {validationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
                {validationError}
              </div>
            )}
            <div className="space-y-3">
              {question.options.map((option) => {
                const isSelected = selectedOptions.includes(option.option_id);
                const maxSelections = question.max_selections || 1;
                const atMaxSelections = selectedOptions.length >= maxSelections;
                const isDisabled = !isSelected && atMaxSelections;
                const isSingleSelect = maxSelections === 1;
                
                return (
                  <div
                    key={option.option_id}
                    onClick={() => !isDisabled && handleOptionSelect(option.option_id)}
                    className={`
                      p-4 rounded-lg border transition-all
                      ${isDisabled
                        ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'bg-purple-50 border-purple-400 text-slate-900 cursor-pointer'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 cursor-pointer'
                      }
                    `}
                    data-testid={`option-${option.option_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {isSingleSelect ? (
                        <div className={`
                          w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                          ${isSelected ? 'border-purple-500 bg-purple-500' : 'border-slate-300'}
                        `}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          className="mt-0.5"
                        />
                      )}
                      <span className="text-base">{option.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={selectedOptions.length === 0 || submitting}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            data-testid="submit-stage-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : sessionData.is_last_stage ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Complete Case
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Exit Case Study?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Your progress has been saved. You can resume this case later from where you left off.
              <br /><br />
              <strong className="text-slate-700">Progress:</strong> Stage {sessionData.current_stage_index + 1} of {sessionData.total_stages}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-slate-700 hover:bg-slate-100 border-slate-200">
              Continue Case
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExit}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Exit &amp; Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
